import "./IotHome.scss";
import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Table } from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faGauge,
  faSearch,
  faPlus,
  faMagnifyingGlassChart,
  faMagnifyingGlass,
  faPenToSquare,
  faTrash,
} from "@fortawesome/free-solid-svg-icons";
import { Iot } from "aws-sdk";

const IotHome = () => {
  const [data, setData] = useState([]);
  const [logCounts, setLogCounts] = useState({});
  const [eventCounts, setEventCounts] = useState({});
  const [loadingProducts, setLoadingProducts] = useState(false);
  const [expandedSections, setExpandedSections] = useState({});
  const [initialLoad, setInitialLoad] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [modalMessage, setModalMessage] = useState("");
  const [modalSubMessage, setModalSubMessage] = useState("");
  const [productIdToDelete, setProductIdToDelete] = useState(null);
  const [searchText, setSearchText] = useState(""); // For search/filtering

  const navigate = useNavigate();

  // Group the data by product number and serial number
  const groupedData = data.reduce((result, item) => {
    const key = `${item.product_type}-${item.product_serial_number}`;
    if (!result[key]) {
      result[key] = [];
    }
    result[key].push(item);
    return result;
  }, {});

  // For search/filtering
  const updateSearchText = (e) => {
    setSearchText(e.target.value); // Step 4: Update search text
  };

  const filterData = () => {
    const lowercasedFilter = searchText.toLowerCase();
    return Object.entries(groupedData)
      .filter(([key, value]) =>
        value.some(
          (item) =>
            item.product_type.toLowerCase().includes(lowercasedFilter) ||
            item.product_serial_number
              .toLowerCase()
              .includes(lowercasedFilter) ||
            item.product_group.toLowerCase().includes(lowercasedFilter) ||
            item.product_description.toLowerCase().includes(lowercasedFilter)
        )
      )
      .reduce((obj, [key, value]) => {
        obj[key] = value;
        return obj;
      }, {});
  };

  // Apply filter before rendering
  const filteredData = filterData();

  const fetchData = async () => {
    try {
      setLoadingProducts(true);
      const response = await fetch("https://sucmhxbjlk.execute-api.us-east-1.amazonaws.com/prod/product/get");
      if (response.ok) {
        const jsonBody = await response.json();
        const jsonData = jsonBody.data;
        setData(jsonData);
        setLoadingProducts(false);

        // Fetch log counts for each product
        const logCounts = await fetchLogCounts(jsonData);
        await fetchEventCounts(jsonData);

        setLoadingProducts(false);

      } else {
        console.error("Failed to fetch data");
      }
    } catch (error) {
      console.error("Error fetching data:", error);
    } finally {
    }
  };

  const fetchLogCounts = async () => {
    try {
      const response = await fetch(
        `https://sucmhxbjlk.execute-api.us-east-1.amazonaws.com/prod/product/logcount`,
        {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
          },
        }
      );
      if (response.ok) {
        const jsonBody = await response.json();
        const jsonData = jsonBody.data;

        const countLogs = jsonData.reduce((counts, event) => {
          const productId = event.product_id;
          counts[productId] = event.log_count;
          return counts;
        }, {});

        setLogCounts(countLogs);
      } else {
        console.error(`Failed to fetch log file counts`);
      }
    } catch (error) {
      console.error(
        `Error fetching log file counts`,
        error
      );
    }
  };

  const fetchEventCounts = async () => {
    try {
      const response = await fetch(
        `https://sucmhxbjlk.execute-api.us-east-1.amazonaws.com/prod/product/events`,
        {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
          },
        }
      );
      if (response.ok) {
        const jsonBody = await response.json();
        const jsonData = jsonBody.data;

        const countEvents = jsonData.reduce((counts, event) => {
          const productId = event.product_id;
          if (counts[productId]) {
            counts[productId]++;
          } else {
            counts[productId] = 1;
          }
          return counts;
        }, {});

        setEventCounts(countEvents);
      } else {
        console.error(`Failed to fetch events`);
      }
    } catch (error) {
      console.error(`Error fetching events`, error);
    }
  };

  useEffect(() => {
    fetchData(); // Fetch data when the component mounts
  }, []); // Empty dependency array to run once when the component mounts

  const handleDeleteClick = (id, serial_number) => {
    setModalMessage(
      `Are you sure you want to remove the serial number "${serial_number}" from the My Products list?`
    );
    setModalSubMessage("This action cannot be undone.");
    setProductIdToDelete(id);
    setShowModal(true);
  };

  const handleModalCancel = () => {
    setShowModal(false);
    setProductIdToDelete(null);
  };

  const handleModalDelete = () => {
    if (productIdToDelete !== null) {
      deleteProductFunction(productIdToDelete);
    }
    setShowModal(false);
  };

  const handleEscKey = (event) => {
    if (event.key === "Escape") {
      handleModalCancel();
    }
  };

  useEffect(() => {
    if (showModal) {
      window.addEventListener("keydown", handleEscKey);
    } else {
      window.removeEventListener("keydown", handleEscKey);
    }

    return () => {
      window.removeEventListener("keydown", handleEscKey);
    };
  }, [showModal]);

  const deleteProductFunction = async (id) => {
    try {
      const response = await fetch(
        `https://sucmhxbjlk.execute-api.us-east-1.amazonaws.com/prod/product/delete/${id}`,
        {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
          }
        }
      );
      if (response.ok) {
        const jsonBody = await response.json();
        const jsonData = jsonBody.data;

        // Update the data state
        setData(jsonData);
      } else {
        console.error("Failed to delete product");
      }
    } catch (error) {
      console.error("Error deleting product:", error);
    }
  };

  useEffect(() => {
    if (
      initialLoad &&
      Object.keys(expandedSections).length === 0 &&
      Object.keys(groupedData).length > 0
    ) {
      const firstKey = Object.keys(groupedData)[0];
      setExpandedSections({ [firstKey]: true });
      setInitialLoad(false);
    }
  }, [expandedSections, initialLoad, groupedData]);

  const getProductImage = (productName) => {
    try {
      const EvoModels = [
        "EVO-2212",
        "EVO-2224",
        "EVO-3012",
        "EVO-4024",
        "EVO-2212E",
        "EVO-2224E",
        "EVO-3012E",
        "EVO-4024E",
      ];
      const EvoSPModels = ["EVO-4248", "EVO-4248SP"];

      let productImage = "EVO.png";
      if (EvoSPModels.includes(productName)) productImage = "EVO-SP.png";

      const imagePath = `/products/${productImage}`;
      return imagePath;
    } catch (e) {
      return `/products/default.png`;
    }
  };

  for (const key in groupedData) {
    groupedData[key].sort(
      (a, b) => new Date(b.datetime_file) - new Date(a.datetime_file)
    );
  }

  const handleAddProductClick = () => {
    navigate("/product/add");
  };

  const handleEditProductClick = (product) => {
    navigate("/product/edit/" + product.id, { state: { product } });
  };

  const handleLogsClick = (product) => {
    navigate("/logs/" + product.id, { state: { product } });
  };

  const handleDebugClick = (product) => {
    navigate("/debug/" + product.id, { state: { product } });
  };

  const handleDashboardClick = (product) => {
    navigate("/dashboard/" + product.id, { state: { product } });
  };

  return (
    <div className="homeContainer">
      <div className="homeHeader">
        <div className="homeHeaderTitle">
          <h1>My Samlex Devices</h1>
        </div>
        <div className="homeHeaderSearch">
          <div className="homeHeaderSearchIcon">
            <FontAwesomeIcon icon={faSearch} className="searchIcon" />
          </div>
          <div className="homeHeaderSearchInput">
            <input
              type="text"
              placeholder="Search/Filter..."
              onChange={updateSearchText}
            />
          </div>
        </div>
        <div className="homeHeaderAdd">
          <button onClick={handleAddProductClick}>
            <FontAwesomeIcon icon={faPlus} />
            Register Product
          </button>
        </div>
      </div>
      <div className="homeTableContainer">
        {loadingProducts ? (
          <p>Loading...</p>
        ) : (
          <div className="homeTableScroll">
            <div className="homeTableContent">
              <Table striped bordered hover>
                <thead>
                  <tr>
                    <th>IoT Device</th>
                    <th>EVO Model</th>
                    <th>EVO SN#</th>
                    <th>Group</th>
                    <th>Description</th>
                    <th>Logs Available</th>
                    <th>Events</th>
                    <th>Operations</th>
                  </tr>
                </thead>
                {showModal && (
                  <div className="modalContainer">
                    <div className="modalBackdrop">
                      <div className="modalPopup">
                        <p>{modalMessage}</p>
                        <p>{modalSubMessage}</p>
                        <div className="buttonContainer">
                          <button
                            className="modalButton"
                            onClick={handleModalCancel}
                          >
                            Cancel
                          </button>
                          <button
                            className="modalButton"
                            onClick={handleModalDelete}
                          >
                            Confirm
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
                <tbody>
                  {Object.keys(filteredData).length > 0 ? (
                    Object.keys(filteredData).map((key, index) => (
                      <React.Fragment key={index}>
                        <tr>
                          <td>
                            <div className="iotCell">
                              {groupedData[key][0].id}
                            </div>
                          </td>
                          <td>
                            <div className="productCell">
                              <img
                                src={getProductImage(
                                  groupedData[key][0].product_type
                                )}
                                alt={groupedData[key][0].product}
                                width="50"
                                height="50"
                              />
                              {groupedData[key][0].product_type}
                            </div>
                          </td>
                          <td>{groupedData[key][0].product_serial_number}</td>
                          <td>
                            <div>{groupedData[key][0].product_group}</div>
                          </td>
                          <td style={{ width: "425px" }}>
                            <div>{groupedData[key][0].product_description}</div>
                          </td>
                          <td style={{ textAlign: "center" }}>
                            {logCounts[groupedData[key][0].id] !== undefined
                              ? logCounts[groupedData[key][0].id]
                              : "..."}
                          </td>
                          <td style={{ textAlign: "center" }}>
                            {eventCounts[groupedData[key][0].id] !== undefined
                              ? eventCounts[groupedData[key][0].id]
                              : "..."}
                          </td>
                          <td>
                            <div className="dataCell">
                              <div className="buttonCell">
                                <button
                                  className="button"
                                  onClick={() =>
                                    handleDashboardClick(groupedData[key][0])
                                  }
                                >
                                  <FontAwesomeIcon icon={faGauge} />
                                  Dashboard
                                </button>
                                <button
                                  className="button"
                                  onClick={() =>
                                    handleDebugClick(groupedData[key][0])
                                  }
                                >
                                  <FontAwesomeIcon icon={faMagnifyingGlass} />
                                  Investigate
                                </button>
                                <button
                                  className="button"
                                  onClick={() =>
                                    handleLogsClick(groupedData[key][0])
                                  }
                                >
                                  <FontAwesomeIcon
                                    icon={faMagnifyingGlassChart}
                                  />
                                  Analyze
                                </button>
                                <button
                                  className="button"
                                  onClick={() =>
                                    handleEditProductClick(groupedData[key][0])
                                  }
                                >
                                  <FontAwesomeIcon icon={faPenToSquare} />
                                  Edit
                                </button>
                                <button
                                  className="button"
                                  onClick={() =>
                                    handleDeleteClick(
                                      groupedData[key][0].id,
                                      groupedData[key][0].serial_number
                                    )
                                  }
                                >
                                  <FontAwesomeIcon icon={faTrash} />
                                  Delete
                                </button>
                              </div>
                            </div>
                          </td>
                        </tr>
                      </React.Fragment>
                    ))
                  ) : (
                    <tr>
                      <td colSpan="6">No data available</td>
                    </tr>
                  )}
                </tbody>
              </Table>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default IotHome;
